import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { getCookie, resetTokensAndSendToLogin, setCookie } from 'helpers/cookies';
import users from './users';
import { IRefundStatusResponse, IRefundPayload } from 'interfaces';
import { functionCollectBrowserData } from 'helpers/browserData/functionCollectBrowserData';
import { refresTokensHandle } from 'helpers/tokens';

export const paymentUrl = window.REACT_APP_BILLING_URL !== 'REPLACE_REACT_APP_BILLING_URL' ? window.REACT_APP_BILLING_URL : process.env.REACT_APP_BILLING_URL || 'billing_url';

const correctHeaders = window.location?.href?.includes('localhost:3000')
  ? { "x-forwarded-for": "0.0.0.0", "Access-Control-Allow-Origin": "*"}
  : { "Access-Control-Allow-Origin": "*" };

const billingTemplate = axios.create({
  baseURL: window.REACT_APP_BILLING_API !== 'REPLACE_REACT_APP_BILLING_API' ? window.REACT_APP_BILLING_API : process.env.REACT_APP_BILLING_API || 'http://localhost:8020/api',
  // withCredentials: true,
  headers: correctHeaders,
});

billingTemplate.interceptors.request.use(async(config: InternalAxiosRequestConfig) => {
  let accessToken = getCookie('athena-token');
  let decodeInfo: any;
  if (accessToken) {
    try {
      decodeInfo = jwtDecode(accessToken);
    } catch (error) {
      Promise.reject('Error decoding token');
    }
    const expTime = decodeInfo?.exp && decodeInfo.exp * 1000;
    const curTime = new Date().getTime();

    if (expTime && expTime - curTime <= 50000) {
      const refresh_token = getCookie('refresh_token');      
      if (refresh_token) {
        try {
          const newTokens = await refresTokensHandle(refresh_token);
          accessToken = newTokens.access_token;
        } catch (error) {
          Promise.reject('Error refreshing tokens');
          resetTokensAndSendToLogin();
        };
      };
    };

    config.headers!.Authorization = `Bearer ${accessToken}`
  } else {
    setCookie('athena-token', '', 1);
  }
  return config;
});

let cntTryingUpdateFingerprint = 0;

billingTemplate.interceptors.response.use(( response: AxiosResponse ) => {
  return response
}, async (error: AxiosError<any>) => {
  if (error.response?.status === 401 && cntTryingUpdateFingerprint < 3) {
    cntTryingUpdateFingerprint++;

    try {
      const formData = new FormData();
      const browserData = await functionCollectBrowserData();
      formData.append('browser_data', JSON.stringify(browserData) || '');

      const freshTokens = await users.updateFingerprint(formData);
      setCookie('athena-token', freshTokens.data.access_token, 1);
      setCookie('refresh_token', freshTokens.data.refresh_token, 1);

      return billingTemplate.request(error.request.responseURL);
    } catch {
      resetTokensAndSendToLogin();
    }
  } else {
    console.log(error, 'Billing error');
  };
  return Promise.reject(error)
});

// Billing
const registerBill = (payload: any) => {
  const params = new URLSearchParams({
    id: payload.tariff_id,
    cost: payload.cost,
    ...(payload.is_recurring && { is_recurring: payload.is_recurring }),

  });
  return billingTemplate.post<any>(`/tariffs/register/?${params.toString()}`, {
    constraints: payload.constraints,
    payment_type: payload.payment_type,
    ...(payload.is_recurring && { is_recurring: payload.is_recurring }),
  });
};

const registerBillV2 = (payload: any) => {
  const params = new URLSearchParams({
    id: payload.tariff_id,
    cost: payload.cost,
    room_name: payload.room_name,
    ...(payload.room_description && { room_description: payload.room_description }),
    ...(payload.is_recurring && { is_recurring: payload.is_recurring, allow_recurring_payments: payload.allow_recurring_payments }),
  });
  if (payload.quote_name) {
    params.append('quote_name', payload.quote_name);
  }
  return billingTemplate.post<any>(
    `/tariffs/register/v2/?${params.toString()}`,
    {
      constraints: payload.constraints,
      payment_type: payload.payment_type,
      ...(payload.quote_name && { quote_name: payload.quote_name }),
      ...(payload.is_recurring && { is_recurring: payload.is_recurring, allow_recurring_payments: payload.allow_recurring_payments }),
    }
  );
};

const extendBill = (
  payload: any
) => billingTemplate.post<any>(`/tariffs/extend/`, payload);

const extendBillV2 = (
  payload: any
) => billingTemplate.post<any>(`/tariffs/extend/v2/`, payload);

const updateBill = (
  payload: any
) => billingTemplate.post<any>(`/tariffs/update/`, payload);

const validateQuoteName = (
  quote_name: string,
  plan_id: string
) => billingTemplate.get<{ exists: boolean }>(`/tariffs/validate-name/?quote_name=${quote_name}&plan_id=${plan_id}`);

const changeQuote = (payload: { room_id: string; quote_id: string }) => {
  const params = new URLSearchParams({
    room_id: payload.room_id,
    quote_id: payload.quote_id,
  });
  return billingTemplate.post<any>(`/tariffs/change-quote/?${params.toString()}`);
};

const cancelSubscription = (subscriptionId: string) =>
  billingTemplate.patch<any>(`/tariffs/history/${subscriptionId}/`, {
    is_recurring: false,
  });

const updateRecurringStatus = (subscriptionId: string, is_recurring: boolean) =>
  billingTemplate.patch<any>(`/tariffs/history/${subscriptionId}/`, {
    is_recurring,
  });

const getActiveSubscriptions = () =>
  billingTemplate.get<any[]>(`/tariffs/history/active/`);

const getTariffHistory = () =>
  billingTemplate.get<any[]>(`/tariffs/history/`);

const changeSubscriptionPaymentMethod = (
  quoteId: string,
  paymentService: string
) =>
  billingTemplate.post<any>(
    `/tariffs/subscriptions/${quoteId}/payment-method/`,
    {
      payment_service: paymentService,
    }
  );
const checkRefund = (tariff_history_id: string) =>
  billingTemplate.get<IRefundStatusResponse>(`/tariffs/billing/refund/check/`, {
    params: { tariff_history_id },
  });

const createRefund = (payload: IRefundPayload) =>
  billingTemplate.post<{ success: boolean }>(`/tariffs/billing/refund/`, payload);

const billing = {
  registerBill,
  registerBillV2,
  extendBill,
  updateBill,
  extendBillV2,
  validateQuoteName,
  changeQuote,
  cancelSubscription,
  updateRecurringStatus,
  getActiveSubscriptions,
  getTariffHistory,
  changeSubscriptionPaymentMethod,
  checkRefund,
  createRefund,
};

export default billing;

